import { Button } from '@creditornot/cb-button';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { useIsMobile } from 'common/hooks/useIsMobile';

import { CampaignAction } from '../../types';
import ConfirmDeleteModal from '../modals/ConfirmDeleteModal';
import ConfirmTerminateModal from '../modals/ConfirmTerminateModal';

export type CampaignActionsFooterProps = {
  campaignId?: string;
  isLoading?: boolean;
  isEditMode?: boolean;
  availableActions?: CampaignAction[] | undefined;
  onCreate?(): void;
  onUpdate?(campaignId: string): void;
  onClone?(campaignId: string): void;
  onDelete?(campaignId: string): void;
  onTerminate?(campaignId: string): void;
  onCancel?(): void;
};

const StyledWrapper = styled.div<{ isMobile?: boolean }>`
  display: flex;
  gap: 16px;
  justify-content: space-between;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
`;

const StyleLeftWrapper = styled.div<{ isMobile?: boolean; alignRight: boolean }>`
  display: flex;
  gap: 16px;
  justify-content: ${({ alignRight }) => (alignRight ? 'flex-end' : 'flex-start')};
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  flex: 1;
`;

export const CampaignActionsFooter = ({
  campaignId,
  isLoading = false,
  isEditMode = false,
  availableActions,
  onCreate,
  onClone,
  onDelete,
  onTerminate,
  onUpdate,
  onCancel,
}: CampaignActionsFooterProps) => {
  const [isTerminateModalOpen, setIsTerminateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const isMobile = useIsMobile();
  const buttonsSize = isMobile ? 'large' : 'small';

  const isCampaignEndable = availableActions?.includes('terminate');
  const isCampaignEditable = availableActions?.includes('edit');
  const isCampaignDeletable = availableActions?.includes('delete');
  const isCampaignCloneable = availableActions?.includes('clone');

  if (!campaignId) {
    return (
      <Button variant="blue" size="large" stretch onClick={onCreate} loading={isLoading} disabled={isLoading}>
        <FormattedMessage id="campaigns.form.create-campaign" />
      </Button>
    );
  }

  return (
    <StyledWrapper isMobile={isMobile}>
      <ConfirmTerminateModal
        isOpen={isTerminateModalOpen}
        onTerminate={() => {
          onTerminate?.(campaignId);
          setIsTerminateModalOpen(false);
        }}
        onBack={() => setIsTerminateModalOpen(false)}
      />
      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onDelete={() => {
          onDelete?.(campaignId);
          setIsDeleteModalOpen(false);
        }}
        onBack={() => setIsDeleteModalOpen(false)}
      />
      <StyleLeftWrapper isMobile={isMobile} alignRight={!isCampaignEditable}>
        {isCampaignDeletable && (
          <Button variant="lightRed" size={buttonsSize} stretch={isMobile} onClick={() => setIsDeleteModalOpen(true)}>
            <FormattedMessage id="campaigns.delete" />
          </Button>
        )}
        {isCampaignEndable && (
          <Button variant="lightRed" size={buttonsSize} onClick={() => setIsTerminateModalOpen(true)}>
            <FormattedMessage id="campaigns.end" />
          </Button>
        )}
        {!isEditMode && isCampaignCloneable && (
          <Button variant="lightBlue" size={buttonsSize} onClick={() => onClone?.(campaignId)}>
            <FormattedMessage id="campaigns.clone" />
          </Button>
        )}
      </StyleLeftWrapper>

      {isEditMode && (
        <Button variant="lightBlue" size={buttonsSize} onClick={onCancel}>
          <FormattedMessage id="state.cancel" />
        </Button>
      )}

      {isCampaignEditable && onUpdate && (
        <Button
          variant="blue"
          size={buttonsSize}
          onClick={() => onUpdate?.(campaignId)}
          loading={isLoading}
          disabled={isLoading}
        >
          <FormattedMessage id={isEditMode ? 'campaigns.save-changes' : 'campaigns.edit-campaign'} />
        </Button>
      )}
    </StyledWrapper>
  );
};
